import { Component, effect, inject, signal } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { DisplayMessageComponent } from "./components/display_message";
import { OffCanvasMenuComponent } from "./components/off_canvas_menu";
import { ChannelService, TvService } from "./services";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { debounceTime, fromEvent } from "rxjs";
import { Theme } from 'app/models/display'
import { CastReceiverService } from "app/services/cast_receiver_service"

@Component({
  selector: 'app',
  templateUrl: './app.html',
  standalone: true,
  imports: [RouterOutlet, DisplayMessageComponent, OffCanvasMenuComponent]
})
export class AppComponent {
  pageTitle = 'InfluxTv'
  topBarVisible = signal(false)
  isOpened = signal(false)

  private tvService = inject(TvService)
  private channelService = inject(ChannelService)
  private router = inject(Router)
  tv = toSignal(this.tvService.currentTv$)

  private _appliedChannel: string
  private _appliedThemeStamp: number
  private _originalBackground: string
  sheet: CSSStyleSheet

  constructor () {
    // The state doc is desired state: whenever its channel differs from the one
    // we last applied (including on boot), navigate the TV to that screen.
    effect(() => {
      const tv = this.tv()
      if (!tv?.channel) { return }
      const channel = JSON.stringify(tv.channel)
      if (channel !== this._appliedChannel) {
        const first = this._appliedChannel === undefined
        this._appliedChannel = channel
        // Registration lands on /welcome — let it stay until dismissed
        if (first && this.router.url.includes('/welcome')) { return }
        this.channelService.process(tv.channel)
      }
    })

    effect(() => {
      const theme = this.tv()?.theme
      if (theme?.updated === this._appliedThemeStamp) { return }
      this._appliedThemeStamp = theme?.updated
      this.changeTheme(theme)
    })

    this.initMouseTracking()
    inject(CastReceiverService).initializeReceiver()
  }

  toggleMenu () {
    this.isOpened.update((open) => !open)
  }

  private initMouseTracking () {
    const mouseMove$ = fromEvent(document, 'mousemove');
    // If mouse moves, then the menu should show if we have a TV
    mouseMove$.pipe(takeUntilDestroyed()).subscribe(() => { this.topBarVisible.set(!!this.tv()) })
    // If the mouse stops moving, hide the top menu
    mouseMove$
      .pipe(
        debounceTime(5000),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.topBarVisible.set(false)
        this.isOpened.set(false)
      })
  }

  private changeTheme (theme: Theme) {
    // Grab the default, then we go back to that if the TV is de-authd
    if(!this._originalBackground) {
      this._originalBackground = getComputedStyle(document.body).backgroundImage;
    }
    document.body.style.backgroundImage = (theme?.background_url) ? `url(${theme.background_url})` : this._originalBackground
    if(theme) {
      this.loadTheme(theme)
    } else {
      this.clearStyleSheet()
    }
  }

  private loadTheme (theme: Theme) {
    if (!theme?.header) { return }
    // https://davidwalsh.name/add-rules-stylesheets
    // Works but need to change the font when it the attribute changes
    //    WebFont.load(window.WebFontConfig)
    window.WebFont.load({ google: { families: [`${ theme.title.font_type }::latin`, `${ theme.header.font_type }::latin`, `${ theme.text.font_type }::latin`, `${ theme.notes.font_type }::latin`] } })

    if (this.sheet) {this.clearStyleSheet()} else {this.prepareStyleSheet()}

    this.sheet.insertRule(`.display-wrapper {background-color:${ theme.background_color };}`, 0)
    this.sheet.insertRule(`.display-wrapper h1 {font-family:'${ theme.title.font_type.replace(/\+/g, ' ') }' !important; color: ${ theme.title.font_color };}`, 1)
    this.sheet.insertRule(`.display-wrapper h2 {font-family:'${ theme.header.font_type.replace(/\+/g, ' ') }' !important; color: ${ theme.header.font_color };}`, 1)
    this.sheet.insertRule(`.display-wrapper {font-family:'${ theme.text.font_type.replace(/\+/g, ' ') }' !important; color: ${ theme.text.font_color };`, 2)
    this.sheet.insertRule(`.display-wrapper .notes {font-family:'${ theme.notes.font_type.replace(/\+/g, ' ') }' !important; color: ${ theme.notes.font_color };`, 3)
  }

  private prepareStyleSheet (){
    const style = document.createElement('style')
    // WebKit hack :(
    style.appendChild(document.createTextNode(''))
    // Add the <style> element to the page
    document.head.appendChild(style)
    this.sheet = style.sheet as CSSStyleSheet
    // sheet.insertRule(".tv-view, .tv-view h1 {font-family:'#{this.currentTv.font_type}' !important;color:lime !important;}",0)
  }

  private clearStyleSheet () {
    for (let i = this.sheet?.cssRules.length - 1; i >= 0; i--) {
      this.sheet.deleteRule(i);
    }
  }
}
